// Landing page for the deletion-request confirmation link
// (/data-deletion/confirm/:token). Visiting this URL confirms the request
// and shows the requester their reference number — their proof it landed.
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import {
	Alert,
	Box,
	CircularProgress,
	Container,
	Paper,
	Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export const DataDeletionConfirmPage: React.FC = () => {
	const { token } = useParams<{ token: string }>();
	const [reference, setReference] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const confirm = async () => {
			try {
				const response = await fetch("/api/v2/deletion-request/confirm/", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ token }),
				});
				if (response.status === 410) {
					const gone = (await response.json()) as { detail: string };
					setError(gone.detail);
					return;
				}
				if (!response.ok) {
					throw new Error(`${response.status} ${response.statusText}`);
				}
				const data = (await response.json()) as { reference: string };
				setReference(data.reference);
			} catch (err) {
				setError(
					`Could not confirm the request: ${err instanceof Error ? err.message : String(err)}`
				);
			}
		};
		void confirm();
	}, [token]);

	return (
		<Box sx={{ minHeight: "100vh", bgcolor: "grey.50", py: { xs: 3, sm: 6 } }}>
			<Container component="main" maxWidth="sm">
				<Paper
					data-component="deletion-confirm-landing"
					elevation={0}
					sx={{
						p: { xs: 3, sm: 4 },
						borderRadius: 2,
						textAlign: "center",
						border: "2px solid",
						borderColor: "primary.main",
					}}
				>
					{error !== null ? (
						<Alert severity="error">{error}</Alert>
					) : reference === null ? (
						<CircularProgress data-component="deletion-confirm-loading" />
					) : (
						<>
							<CheckCircleOutlined
								sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
							/>
							<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
								Your request is confirmed
							</Typography>
							<Typography
								variant="body1"
								color="text.secondary"
								sx={{ mb: 2 }}
							>
								An operator now reviews it. You will receive an email
								when the deletion is complete.
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Your reference number:
							</Typography>
							<Typography
								data-component="deletion-confirm-reference"
								variant="h6"
								sx={{ fontWeight: 600 }}
							>
								{reference}
							</Typography>
						</>
					)}
				</Paper>
			</Container>
		</Box>
	);
};
